import { track } from '@/common/track';
import { KLAYO_COLORS } from '@/themes';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { JobBuilderEvent } from 'klayowebshared';
import { ReactNode } from 'react';
import AddIcon from '../../resources/images/icons-add.svg';
import AiSparkleIcon from '../../resources/images/icons-ai-sparkle.svg';
import UploadIcon from '../../resources/images/icons-upload.svg';
import { BuildMode } from '../../views/job-builder/jobBuilder.types';

interface CreateJobModePickerProps {
  selectedMode: BuildMode;
  onModeChange: (mode: BuildMode) => void;
  children: ReactNode;
  translate: (key: string) => string;
}

export function CreateJobModePicker({
  selectedMode,
  onModeChange,
  children,
  translate,
}: CreateJobModePickerProps) {
  const handleSelectMode = (mode: BuildMode) => {
    if (mode !== selectedMode) {
      track(
        mode === BuildMode.KI
          ? JobBuilderEvent.SelectIntelligence
          : mode === BuildMode.DESCRIPTION
            ? JobBuilderEvent.SelectJd
            : JobBuilderEvent.SelectScratch
      );
    }
    onModeChange(mode);
  };

  const modeCards = [
    {
      id: BuildMode.KI,
      icon: <AiSparkleIcon width={32} height={32} color={KLAYO_COLORS.NeonBlueNB500} />,
      title: translate('jobBuilder.startWithKI'),
      subtitle: translate('jobBuilder.startWithKISubtitle'),
    },
    {
      id: BuildMode.DESCRIPTION,
      icon: <UploadIcon width={32} height={32} color={KLAYO_COLORS.NeonBlueNB500} />,
      title: translate('jobBuilder.startFromDescription'),
      subtitle: translate('jobBuilder.startFromDescriptionSubtitle'),
    },
    {
      id: BuildMode.SCRATCH,
      icon: <AddIcon width={32} height={32} color={KLAYO_COLORS.NeonBlueNB500} />,
      title: translate('jobBuilder.startFromScratch'),
      subtitle: translate('jobBuilder.startFromScratchSubtitle'),
    },
  ];

  return (
    <Stack gap={5} flex={1} display='flex' flexDirection='column'>
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={4}>
        {modeCards.map((card) => {
          const isSelected = selectedMode === card.id;
          return (
            <Paper
              key={card.id}
              variant='outlined'
              onClick={() => handleSelectMode(card.id)}
              sx={{
                flex: 1,
                p: '24px 32px',
                borderRadius: '16px',
                borderWidth: isSelected ? 2 : 1,
                borderColor: isSelected ? KLAYO_COLORS.NeonBlueNB500 : KLAYO_COLORS.NeonBlueNB100,
                cursor: 'pointer',
                '&:hover': { borderColor: KLAYO_COLORS.NeonBlueNB500 },
                backgroundColor: isSelected ? KLAYO_COLORS.NeonBlueNB025 : KLAYO_COLORS.WhiteWH900,
              }}
            >
              <Stack gap={2}>
                <Box sx={{ '& svg path[fill="#111827"]': { fill: KLAYO_COLORS.NeonBlueNB500 } }}>
                  {card.icon}
                </Box>
                <Stack gap={0.5}>
                  <Typography
                    variant='h6'
                    fontSize={18}
                    fontWeight={600}
                    color={KLAYO_COLORS.GreyGR900}
                  >
                    {card.title}
                  </Typography>
                  <Typography variant='body2' color={KLAYO_COLORS.GreyGR500}>
                    {card.subtitle}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>
      <Box flex={1} display='flex' flexDirection='column'>
        {children}
      </Box>
    </Stack>
  );
}
